import i18n from '@i18next/i18n';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import Snackbar from '@tencent/dui/lib/components/Snackbar';
import MobileSnackbar from '@tencent/dui-mobile/lib/components/Snackbar';
import { ImageEditErrors } from '../errors';
import { ImageEditEventHandlers, ImageEditEvents } from '../events';
import { Emitter } from 'nanoevents';
import { isSVip } from '@src/js/common/ark-extensions/dc-components/vip-linktext';
import { showDialogWithCode, ESpaceAidType } from '@src/js/common/ark-extensions/dc-components/docs-component-size-limit-dialog';
import { DialogType } from '@src/js/common/ark-extensions/dc-components/docs-component-size-limit-dialog/types';

type ToastOptions = Parameters<typeof Snackbar.show>[0] & Parameters<typeof MobileSnackbar.show>[0];

const SAVING_DELAY = 1000;

function toast(options: ToastOptions) {
    if (browser.isMobile) {
        return MobileSnackbar.show(options);
    }
    return Snackbar.show(options);
}

export function registerSnackbar(eventBus: Emitter<ImageEditEventHandlers>) {
    let pending = 0;
    let loadingBar;
    let loadingBarDelay: null | ReturnType<typeof setTimeout> = null;

    function closeLoadingBar() {
        if (loadingBarDelay) {
            clearTimeout(loadingBarDelay);
        }

        loadingBar?.close();
        loadingBar = null;
    }

    eventBus.on(ImageEditEvents.WillCreateImages, () => {
        pending += 1;
        closeLoadingBar();

        loadingBarDelay = setTimeout(() => {
            if (pending && !loadingBar) {
                loadingBar = toast({
                    autoClose: false,
                    type: 'loading',
                    message: i18n.t('正在保存图片...'),
                });
            }
        }, SAVING_DELAY);
    });

    eventBus.on(ImageEditEvents.DidCreateImages, () => {
        pending = Math.max(0, pending - 1);

        if (!pending) {
            closeLoadingBar();
        }
    });

    eventBus.on(ImageEditEvents.CreateImageError, (errs) => {
        const isOffline = errs.find(err => err.message === ImageEditErrors.networkOffline);
        if (isOffline) {
            toast({
                duration: 5000,
                type: 'info',
                message: ImageEditErrors.networkOffline,
            });
        } else {
            toast({
                duration: 5000,
                type: 'info',
                message: ImageEditErrors.broken,
            });
        }
    });

    eventBus.on(ImageEditEvents.ValidateError, (err) => {
        toast({
            duration: 5000,
            type: 'info',
            message: err.message,
        });
    });

    eventBus.on(ImageEditEvents.UploadError, (err: Error & { retcode?: number }) => {
        if (err.retcode === 20010) {
            showDialogWithCode(err.retcode, {
                type: isSVip() ? DialogType.SIZE_LIMIT_VIP : DialogType.SIZE_LIMIT_OWNER,
            }, ESpaceAidType.INSERT_PIC_SLIDE);

            return;
        }
        if (err.retcode === 20013) {
            showDialogWithCode(err.retcode, {
                type: DialogType.SIZE_LIMIT_COOPERATION_COLLABOR,
            }, ESpaceAidType.INSERT_PIC_SLIDE);
            return;
        }
        if (!err.message) {
            window.log?.error('ImageEditEvents UploadError message is not exist');
            return;
        }
        toast({
            duration: 5000,
            type: 'info',
            message: err.message,
        });
    });
}
